import { GLOBAL } from 'saddlebag-browser';
import logger from 'saddlebag-logger';
import observer from 'saddlebag-observer';
import { initTrackingPreferences } from 'saddlebag-tracking-preferences';

import {
  init as initBehaviouralSDK,
  stopPageTransition,
} from '../../core/src/modern/index';

import { clearPreferencesCookie } from './clearPreferencesCookie';
import {
  sendFunnelClientsViewEvent,
  sendFunnelAcquisitionEvent,
  sendExperimentUserContextEvent,
  sendUserPreferencesEvent,
} from './funnelEvents';
import { getTrackIdentifier } from './getTrackIdentifier';
import { initAsync as grapplerAsync } from './grappler/grappler';
import { syncExperimentationCookies } from './syncExperimentationCookies';
import { sendViewViewEvent } from './viewViewEvent/sendViewViewEvent';

const doc = GLOBAL.getDocument();
const trackingScript = doc.getElementById('trackolding-sync');
const data = JSON.parse(trackingScript.getAttribute('data-data'));

function initWindowData() {
  const $window = GLOBAL.getWindow();
  if (!$window.__trackolding) $window.__trackolding = {};
  if (!$window.__trackolding.getTrackIdentifier)
    $window.__trackolding.getTrackIdentifier = getTrackIdentifier;
}

function markPageTransition() {
  const $window = GLOBAL.getWindow();
  if ($window?.__internal?.screenViewEventGuid) {
    stopPageTransition($window.__internal.screenViewEventGuid);
  }
}

try {
  initTrackingPreferences({
    enableKetchCMP: data.config.cmp?.enableKetchCMP || false,
    consumer:
      data.config.cmp?.consumer || data.config.service_name || 'trackolding',
    enablePurposeScopedIdService:
      data.config.cmp?.enablePurposeScopedIdService || false,
    ketchConsentCookieName: data.config.cmp?.ketchConsentCookieName,
  });
} catch (error) {
  logger.logError(error, {
    libraryName: 'trackolding',
    component: 'trackolding-initTrackingPreferences',
  });
}

const bootstrapTracking = () => {
  // TODO: Remove this once the presence of the cookie in traveller browsers is negligible https://skyscanner.atlassian.net/browse/ZIGGY-4663
  clearPreferencesCookie();
};

logger.logOperationalEvent({ eventName: 'Trackolding_init' });

try {
  initBehaviouralSDK({
    environment: data.config.env || 'public',
    behavioural: data.config.behavioural,
    page_name: data.config.page_name,
    service_name: data.config.service_name,
  });
} catch (error) {
  logger.logError(error);
}

try {
  initWindowData();
  bootstrapTracking();

  const {
    config,
    culture,
    device,
    featureTests,
    microsite,
    pageContext,
    requestId,
    userInfo,
    viewGuid,
    webNodeOverride,
  } = data;

  const viewViewData = {
    culture,
    device,
    featureTests,
    micrositeName: microsite.name,
    pageContext,
    requestId,
    userInfo,
    viewGuid,
    webNodeOverride,
  };

  const clientViewData = {
    allowedQueryParams: pageContext?.allowedQueryParams,
    analyticsPageName: pageContext?.analyticsPageName,
    analyticsPageUrl: pageContext?.gaPageView,
    config,
    culture,
    micrositeName: microsite.name,
    pageInstance: pageContext?.pageInstance,
    pageType: pageContext?.pageType,
    requestId,
    isInternalUser: userInfo.isInternalUser,
    viewGuid,
    webNodeOverride,
  };

  const { is_new_session: isNewSession, spaMode } = config;

  // Initial emission: only for a new session, so a returning session's page
  // load doesn't re-send the opening UserPreferences.
  if (isNewSession) {
    sendUserPreferencesEvent(data);
  }

  sendExperimentUserContextEvent(data);

  // Consent-change emissions: always fire, regardless of session age. When the
  // cookie banner updates preferences the payload's consent flags have changed,
  // so this is genuinely new information — not a duplicate of the init send.
  observer.subscribe('tracking-preferences-changed', () =>
    sendUserPreferencesEvent(data),
  );
  observer.subscribe('user-cookie-preferences-saved', () =>
    sendUserPreferencesEvent(data),
  );

  if (!spaMode) {
    sendViewViewEvent(viewViewData).then(markPageTransition);
    sendFunnelClientsViewEvent(clientViewData);
    sendFunnelAcquisitionEvent(data);
  }

  if (spaMode) {
    observer.subscribe('trackolding-spa-acquisition', ({ virtualReferrer }) => {
      sendFunnelAcquisitionEvent({
        ...data,
        virtualReferrer,
      });
    });

    observer.subscribe(
      'trackolding-spa-page-viewed',
      ({
        pageContext: newPageContext,
        previousName,
        referrerId,
        requestId: newRequestId,
        viewGuid: newViewGuid,
        virtualReferrer,
      }) => {
        sendFunnelClientsViewEvent({
          ...clientViewData,
          analyticsPageName: newPageContext?.analyticsPageName,
          analyticsPageUrl: newPageContext?.gaPageView,
          pageType: newPageContext?.pageType,
          previousName,
          referrerId,
          requestId: newRequestId,
          viewGuid: newViewGuid,
          virtualReferrer,
        });
        sendViewViewEvent({
          ...viewViewData,
          pageContext: newPageContext,
          requestId: newRequestId,
          viewGuid: newViewGuid,
          virtualReferrer,
        }).then(markPageTransition);
      },
    );
  }

  observer.subscribe('tracking-preferences-changed', () =>
    syncExperimentationCookies(data),
  );

  observer.publish('trackolding-loaded');

  grapplerAsync(data);
} catch (err) {
  logger.logError(err, {
    libraryName: 'trackolding',
    component: 'trackolding',
  });
}
